/* ============================================================
   Search — matching and ranking, as plain functions over local state.

   The client is offline-first and already holds the WHOLE dataset (`/v1/sync`
   returns every task, habit and check-in, no pagination), so search runs
   locally: results land on the keystroke, they work with no signal, and no
   spinner ever sits between a query and its answer. That's the Things 3 /
   Apple Reminders model, and it's the reason the shipped server-side `?q=`
   (a pg_trgm index on `tasks.name`) isn't what this screen calls — it stays
   the fallback for the day the local set stops being complete.

   A query matches a task's NAME, its TAGS, or its sub-steps, in that order of
   rank — so "errand" finds both the task called Errand and the ones tagged
   `@errand`, with the literal name first. Projects are deliberately not matched:
   a project is a destination you open from the sidebar, and matching one would
   flood the results with everything inside it.

   Kept free of React and of the store so the ranking can be reasoned about —
   and tested — on its own.
   ============================================================ */
import type { Habit, Tag, Task } from './types';

/** Shorter than this and the query is noise: one letter matches nearly every
   row a real library holds, so we show the resting hint instead of a wall. */
export const MIN_QUERY = 2;

/* How many rows each group renders. The projects are drawn in a plain ScrollView
   (no virtualization, like every other list in the app), so an unbounded result
   set for a two-letter query would mount thousands of rows. Live work gets the
   generous caps because that's what you act on; history gets tighter ones
   because looking something up is a "did I ever…" question that the top few
   answers, and a narrower query is one keystroke away. A truncated group says
   so rather than silently lying about its size. */
export const GROUP_CAPS = { habits: 40, open: 60, done: 25, skipped: 25 } as const;

/** Does this engine implement Unicode normalization? Checked once: where it
   exists, "café" and "cafe" should find each other; where it doesn't, we still
   search, just case-insensitively. */
const CAN_FOLD = (() => {
  try {
    return 'é'.normalize('NFD').length === 2;
  } catch {
    return false;
  }
})();

/** Lowercase + strip diacritics — the form both sides of a match are compared
   in, so accents and capitalization never decide whether a row is found. */
export function fold(s: string): string {
  const lower = s.toLowerCase();
  if (!CAN_FOLD) return lower;
  try {
    return lower.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  } catch {
    return lower;
  }
}

/** Score one already-folded term against one already-folded haystack.
   0 = absent. Otherwise where the term landed, because *where* is what makes
   one match better than another: the start of the name beats the start of a
   later word, which beats the middle of one. */
function termScore(haystack: string, term: string): number {
  const i = haystack.indexOf(term);
  if (i < 0) return 0;
  if (i === 0) return 3;
  // a term starting right after any non-alphanumeric is at a word boundary
  return /[a-z0-9]/.test(haystack[i - 1] ?? "") ? 1 : 2;
}

/** Every term must appear (AND, as Todoist and Things both do — extra words
   narrow a search, they never widen it). Returns 0 when any term is missing,
   else the summed positions, so "call dent" ranks "Call dentist" top. */
function allTerms(haystack: string, terms: string[]): number {
  let total = 0;
  for (const term of terms) {
    const s = termScore(haystack, term);
    if (s === 0) return 0;
    total += s;
  }
  return total;
}

/** A name match always outranks a sub-step-only match, whatever the positions:
   the title is what the user was thinking of. */
const NAME_BONUS = 100;

/** A tag match sits BETWEEN the two. Searching "errand" should surface the tasks
   tagged `@errand`, but never above a task actually called "Errand" — the word
   you typed matching a title is a stronger signal than it matching a label the
   title doesn't contain. A project match is deliberately NOT scored: a project is a
   destination you open from the sidebar, and matching one would flood the
   results with every task in it. */
const TAG_BONUS = 50;

export interface TaskHit {
  task: Task;
  score: number;
  /** the sub-step text that matched, when the NAME didn't — the row shows it,
     so a result never looks like it appeared for no reason */
  step?: string;
}

export interface SearchResults {
  habits: Habit[];
  /** live, unresolved tasks — the ones you can still act on */
  open: TaskHit[];
  done: TaskHit[];
  skipped: TaskHit[];
  /** matches found, BEFORE the per-group caps — what "12 results" reports */
  total: number;
  /** per-group count of matches the caps dropped, so a clipped list can say so */
  hidden: { habits: number; open: number; done: number; skipped: number };
}

const EMPTY: SearchResults = {
  habits: [],
  open: [],
  done: [],
  skipped: [],
  total: 0,
  hidden: { habits: 0, open: 0, done: 0, skipped: 0 },
};

/** Score a task: its name first, then its tags, then its sub-steps. */
function scoreTask(task: Task, terms: string[], tagNames: Map<string, string>): TaskHit | null {
  const byName = allTerms(fold(task.name), terms);
  if (byName > 0) return { task, score: NAME_BONUS + byName };
  for (const id of task.tagIds ?? []) {
    const name = tagNames.get(id);
    if (name === undefined) continue;
    const s = allTerms(fold(name), terms);
    // the tag is named on the row already (`tagNote`), so unlike a sub-step
    // match this needs no separate explanation of why the row is here
    if (s > 0) return { task, score: TAG_BONUS + s };
  }
  for (const step of task.subSteps) {
    const s = allTerms(fold(step.t), terms);
    if (s > 0) return { task, score: s, step: step.t };
  }
  return null;
}

/** Sort a resolved group (done / skipped) most-recent-first, since history is
   read backwards from now; score only breaks ties between the same day. */
const byRecency = (dayOf: (t: Task) => string | null | undefined) =>
  (a: TaskHit, b: TaskHit) => {
    const da = dayOf(a.task) ?? '';
    const db = dayOf(b.task) ?? '';
    if (da !== db) return da < db ? 1 : -1;
    return b.score - a.score;
  };

/** Sort live tasks best-match-first, then soonest-first — a dated task ahead of
   an undated one, since a day is a commitment and the backlog isn't. */
function byRelevanceThenDate(a: TaskHit, b: TaskHit): number {
  if (a.score !== b.score) return b.score - a.score;
  const da = a.task.scheduledFor;
  const db = b.task.scheduledFor;
  if (da !== db) {
    if (da === null) return 1;
    if (db === null) return -1;
    return da < db ? -1 : 1;
  }
  return b.task.lastTouched - a.task.lastTouched;
}

/** Split a raw query into folded terms. Exported for the highlighter. */
export function queryTerms(query: string): string[] {
  return fold(query.trim()).split(/\s+/).filter(Boolean);
}

/**
   Search every task and habit the device holds.

   `habits` should be the list derived for the day being viewed (the store's
   `habitsForDay`), not raw definitions — the rows render rings and cadence
   badges from that derived state.
 */
export function searchAll(
  query: string,
  tasks: Task[],
  habits: Habit[],
  tags: Tag[] = [],
): SearchResults {
  const terms = queryTerms(query);
  if (!terms.length || query.trim().length < MIN_QUERY) return EMPTY;

  // id → name, so scoring a task's tags is a lookup rather than a scan per row
  const tagNames = new Map(tags.map((t) => [t.id, t.name]));

  const habitHits: { habit: Habit; score: number }[] = [];
  for (const habit of habits) {
    const score = allTerms(fold(habit.name), terms);
    if (score > 0) habitHits.push({ habit, score });
  }
  habitHits.sort((a, b) => b.score - a.score || a.habit.name.localeCompare(b.habit.name));

  const open: TaskHit[] = [];
  const done: TaskHit[] = [];
  const skipped: TaskHit[] = [];
  for (const task of tasks) {
    const hit = scoreTask(task, terms, tagNames);
    if (!hit) continue;
    // Done and skipped are mutually exclusive resolutions — each clears the
    // other's flags in the store — so the order here only mirrors the one
    // store/logic.ts already reads them in.
    if (task.done) done.push(hit);
    else if (task.skipped) skipped.push(hit);
    else open.push(hit);
  }
  open.sort(byRelevanceThenDate);
  done.sort(byRecency((t) => t.doneAt));
  skipped.sort(byRecency((t) => t.skippedAt));

  const total = habitHits.length + open.length + done.length + skipped.length;
  return {
    habits: habitHits.slice(0, GROUP_CAPS.habits).map((h) => h.habit),
    open: open.slice(0, GROUP_CAPS.open),
    done: done.slice(0, GROUP_CAPS.done),
    skipped: skipped.slice(0, GROUP_CAPS.skipped),
    total,
    hidden: {
      habits: Math.max(0, habitHits.length - GROUP_CAPS.habits),
      open: Math.max(0, open.length - GROUP_CAPS.open),
      done: Math.max(0, done.length - GROUP_CAPS.done),
      skipped: Math.max(0, skipped.length - GROUP_CAPS.skipped),
    },
  };
}
